import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  History,
  Search,
  Filter,
  X,
  ArrowUpDown,
  Package2,
  User,
  Calendar,
  Clock,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  Trash2,
  Plus,
  Edit2
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_FILE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_FILE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterConfig, setFilterConfig] = useState({
    operationType: '', // 'add', 'remove', 'reset'
    dateRange: {
      from: '',
      to: ''
    },
    updatedBy: '',
    productName: '',
    sortBy: 'newest' // 'newest', 'oldest'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Add states for deleted products
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(true);
  const [showDeletedProducts, setShowDeletedProducts] = useState(false);
  
  // Add sort config state
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });

  // Fetch transactions
  useEffect(() => {
    fetchTransactions();
    fetchDeletedProducts();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('id, name, location, image_url, stock_history')
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Transform data to get all transactions from stock_history
      const allTransactions = data.reduce((acc, product) => {
        if (product.stock_history && Array.isArray(product.stock_history)) {
          const productTransactions = product.stock_history.map(history => ({
            ...history,
            product_id: product.id,
            product_name: product.name,
            product_location: product.location,
            product_image: product.image_url
          }));
          return [...acc, ...productTransactions];
        }
        return acc;
      }, []);

      // Sort transactions by date
      allTransactions.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      
      setTransactions(allTransactions);
    } catch (err) {
      setError('İşlem geçmişi yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add function to fetch deleted products
  const fetchDeletedProducts = async () => {
    try {
      setLoadingDeleted(true);
      const { data, error } = await supabase
        .from('deleted_products')
        .select('*')
        .order('deleted_at', { ascending: false });

      if (error) throw error;

      // Transform data for consistent display
      const formattedDeletedProducts = data.map(product => ({
        type: 'delete',
        amount: product.stock,
        previous_stock: product.stock,
        new_stock: 0,
        updated_at: product.deleted_at,
        updated_by: product.deleted_by,
        product_id: product.id,
        product_name: product.name,
        product_location: product.location,
        product_image: product.image_url,
        is_deleted_record: true,
        deleted_at: product.deleted_at,
        original_created_at: product.created_at,
        original_created_by: product.created_by
      }));

      setDeletedProducts(formattedDeletedProducts);
    } catch (err) {
      setError('Silinen ürünler yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoadingDeleted(false);
    }
  };

  // Add a function to handle sorting when column headers are clicked
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Modify the getFilteredTransactions function to include table sorting
  const getFilteredTransactions = () => {
    let filtered = [...transactions];
    
    // Include deleted products if the toggle is on
    if (showDeletedProducts) {
      filtered = [...filtered, ...deletedProducts];
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(transaction =>
        transaction.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.product_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.updated_by.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply operation type filter
    if (filterConfig.operationType) {
      filtered = filtered.filter(transaction => 
        transaction.type === filterConfig.operationType
      );
    }

    // Apply date range filter
    if (filterConfig.dateRange.from) {
      const fromDate = new Date(filterConfig.dateRange.from);
      filtered = filtered.filter(transaction => 
        new Date(transaction.updated_at) >= fromDate
      );
    }
    if (filterConfig.dateRange.to) {
      const toDate = new Date(filterConfig.dateRange.to);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(transaction => 
        new Date(transaction.updated_at) <= toDate
      );
    }

    // Apply updated by filter
    if (filterConfig.updatedBy) {
      filtered = filtered.filter(transaction =>
        transaction.updated_by === filterConfig.updatedBy
      );
    }

    // Apply product name filter
    if (filterConfig.productName) {
      filtered = filtered.filter(transaction =>
        transaction.product_name.toLowerCase().includes(filterConfig.productName.toLowerCase())
      );
    }

    // Apply sorting from filter config (for backwards compatibility)
    if (filterConfig.sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
    } else if (filterConfig.sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }
    
    // Apply table header sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        // Handle different data types
        if (sortConfig.key === 'updated_at' || sortConfig.key === 'deleted_at') {
          // Date sorting
          const aValue = new Date(a[sortConfig.key] || a.updated_at);
          const bValue = new Date(b[sortConfig.key] || b.updated_at);
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else if (sortConfig.key === 'product_name' || sortConfig.key === 'product_location' || sortConfig.key === 'updated_by') {
          // String sorting
          const aValue = a[sortConfig.key] || '';
          const bValue = b[sortConfig.key] || '';
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (sortConfig.key === 'stock_change') {
          // Stock change sorting
          const aChange = a.type === 'add' ? a.amount : a.type === 'remove' ? -a.amount : 0;
          const bChange = b.type === 'add' ? b.amount : b.type === 'remove' ? -b.amount : 0;
          return sortConfig.direction === 'asc' ? aChange - bChange : bChange - aChange;
        } else if (sortConfig.key === 'type') {
          // Transaction type sorting
          const typeOrder = { 'add': 1, 'remove': 2, 'reset': 3, 'create': 4, 'edit': 5, 'delete': 6 };
          const aValue = typeOrder[a.type] || 99;
          const bValue = typeOrder[b.type] || 99;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return 0;
      });
    }

    return filtered;
  };

  // Get unique users who made transactions
  const getUniqueUsers = () => {
    return [...new Set(transactions.map(t => t.updated_by))].filter(Boolean).sort();
  };

  // Get unique product names
  const getUniqueProducts = () => {
    return [...new Set(transactions.map(t => t.product_name))].filter(Boolean).sort();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Belirtilmemiş';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Reset filters
  const resetFilters = () => {
    setFilterConfig({
      operationType: '',
      dateRange: {
        from: '',
        to: ''
      },
      updatedBy: '',
      productName: '',
      sortBy: 'newest'
    });
    setSearchTerm('');
  };

  // Check if any filter is active
  const isFilterActive = () => {
    return (
      filterConfig.operationType !== '' ||
      filterConfig.dateRange.from !== '' ||
      filterConfig.dateRange.to !== '' ||
      filterConfig.updatedBy !== '' ||
      filterConfig.productName !== '' ||
      filterConfig.sortBy !== 'newest'
    );
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filterConfig.operationType !== '') count++;
    if (filterConfig.dateRange.from !== '' || filterConfig.dateRange.to !== '') count++;
    if (filterConfig.updatedBy !== '') count++;
    if (filterConfig.productName !== '') count++;
    if (filterConfig.sortBy !== 'newest') count++;
    return count;
  };

  // Filter Panel Component
  const FilterPanel = () => {
    return (
      <AnimatePresence>
        {showFilterPanel && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 mb-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white">Gelişmiş Filtreler</h3>
              <button 
                onClick={() => setShowFilterPanel(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Operation Type Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">İşlem Tipi</h4>
                <select
                  value={filterConfig.operationType}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, operationType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Tüm İşlemler</option>
                  <option value="add">Stok Girişi</option>
                  <option value="remove">Stok Çıkışı</option>
                  <option value="reset">Stok Sıfırlama</option>
                </select>
              </div>

              {/* Date Range Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Tarih Aralığı</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Başlangıç</label>
                    <input
                      type="date"
                      value={filterConfig.dateRange.from}
                      onChange={(e) => setFilterConfig(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, from: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Bitiş</label>
                    <input
                      type="date"
                      value={filterConfig.dateRange.to}
                      onChange={(e) => setFilterConfig(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, to: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Updated By Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">İşlemi Yapan</h4>
                <select
                  value={filterConfig.updatedBy}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, updatedBy: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Tüm Kullanıcılar</option>
                  {getUniqueUsers().map(user => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </div>

              {/* Product Name Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Ürün Adı</h4>
                <select
                  value={filterConfig.productName}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, productName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Tüm Ürünler</option>
                  {getUniqueProducts().map(product => (
                    <option key={product} value={product}>{product}</option>
                  ))}
                </select>
              </div>

              {/* Sort By Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Sıralama</h4>
                <select
                  value={filterConfig.sortBy}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="newest">En Yeni</option>
                  <option value="oldest">En Eski</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mr-3"
              >
                Filtreleri Temizle
              </button>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Filtreleri Uygula
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // Transaction Details Modal
  const TransactionDetailsModal = ({ transaction, onClose }) => {
    if (!transaction) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header with transaction type badge */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    transaction.type === 'add'
                      ? 'bg-green-100 dark:bg-green-900/20'
                      : transaction.type === 'remove'
                      ? 'bg-red-100 dark:bg-red-900/20'
                      : transaction.type === 'create'
                      ? 'bg-blue-100 dark:bg-blue-900/20'
                      : transaction.type === 'edit'
                      ? 'bg-purple-100 dark:bg-purple-900/20'
                      : transaction.type === 'delete'
                      ? 'bg-red-100 dark:bg-red-900/20'
                      : 'bg-yellow-100 dark:bg-yellow-900/20'
                  }`}>
                    {transaction.type === 'add' && <ArrowUpDown className={`w-5 h-5 text-green-600 dark:text-green-400`} />}
                    {transaction.type === 'remove' && <ArrowUpDown className={`w-5 h-5 text-red-600 dark:text-red-400`} />}
                    {transaction.type === 'reset' && <ArrowUpDown className={`w-5 h-5 text-yellow-600 dark:text-yellow-400`} />}
                    {transaction.type === 'create' && <Plus className={`w-5 h-5 text-blue-600 dark:text-blue-400`} />}
                    {transaction.type === 'edit' && <Edit2 className={`w-5 h-5 text-purple-600 dark:text-purple-400`} />}
                    {transaction.type === 'delete' && <Trash2 className={`w-5 h-5 text-red-600 dark:text-red-400`} />}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    İşlem Detayları
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Transaction type badge */}
              <div className="mb-5">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                  transaction.type === 'add'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : transaction.type === 'remove'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : transaction.type === 'create'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    : transaction.type === 'edit'
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    : transaction.type === 'delete'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {transaction.type === 'add'
                    ? 'Stok Girişi'
                    : transaction.type === 'remove'
                    ? 'Stok Çıkışı'
                    : transaction.type === 'create'
                    ? 'Ürün Ekleme'
                    : transaction.type === 'edit'
                    ? 'Ürün Düzenleme'
                    : transaction.type === 'delete'
                    ? 'Ürün Silme'
                    : 'Stok Sıfırlama'}
                </span>

                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>İşlem tarihi: </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(transaction.updated_at)}</span>
                </div>
              </div>

              <div className="space-y-5">
                {/* Product Info */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Ürün Bilgileri</h4>
                    {transaction.is_deleted_record && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        Silinen Ürün
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Package2 className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ürün Adı:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{transaction.product_name}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-6">Konum:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{transaction.product_location}</span>
                      </div>
                    </div>
                    <div>
                      {transaction.product_image && (
                        <div className="relative h-20 w-20 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-600">
                          <img 
                            src={transaction.product_image} 
                            alt={transaction.product_name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect width='18' height='18' x='3' y='3' rx='2' ry='2'/%3E%3Ccircle cx='9' cy='9' r='2'/%3E%3Cpath d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'/%3E%3C/svg%3E";
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Transaction Info */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">İşlem Detayları</h4>
                  <div className="space-y-3">
                    {transaction.type !== 'delete' ? (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Önceki Stok:</span>
                          <span className="text-sm text-gray-900 dark:text-white">{transaction.previous_stock} Adet</span>
                        </div>
                        {transaction.type !== 'reset' && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">İşlem Miktarı:</span>
                            <span className="text-sm text-gray-900 dark:text-white">
                              {transaction.type === 'add' || transaction.type === 'create' ? '+' : '-'}{transaction.amount} Adet
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Yeni Stok:</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{transaction.new_stock} Adet</span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Değişim:</span>
                            <span className={`text-sm font-medium ${
                              transaction.type === 'add' || transaction.type === 'create' ? 'text-green-600 dark:text-green-400' : 
                              transaction.type === 'remove' ? 'text-red-600 dark:text-red-400' : 
                              'text-yellow-600 dark:text-yellow-400'
                            }`}>
                              {transaction.type === 'add' || transaction.type === 'create'
                                ? `+${transaction.amount} (${((transaction.amount / Math.max(1, transaction.previous_stock)) * 100).toFixed(1)}% artış)` 
                                : transaction.type === 'remove'
                                ? `-${transaction.amount} (${((transaction.amount / transaction.previous_stock) * 100).toFixed(1)}% azalış)`
                                : 'Sıfırlandı'
                              }
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Silinen Stok Miktarı:</span>
                          <span className="text-sm text-red-600 dark:text-red-400 font-medium">{transaction.previous_stock} Adet</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">İşlem Tarihi:</span>
                          <span className="text-sm text-gray-900 dark:text-white">{formatDate(transaction.deleted_at || transaction.updated_at)}</span>
                        </div>
                        {transaction.is_deleted_record && transaction.original_created_at && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ürün Oluşturulma:</span>
                            <span className="text-sm text-gray-900 dark:text-white">{formatDate(transaction.original_created_at)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* İşlem Açıklaması */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">İşlem Açıklaması</h4>
                  <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                    {transaction.type === 'add' && (
                      <div className="flex items-start gap-2">
                        <div className="p-1 bg-green-100 dark:bg-green-900/20 rounded-full mt-0.5">
                          <ArrowUpDown className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <p>
                          <span className="font-medium">{transaction.amount} adet</span> ürün stoka eklendi. 
                          Stok miktarı <span className="font-medium">{transaction.previous_stock}</span> adetten 
                          <span className="font-medium"> {transaction.new_stock}</span> adete yükseltildi.
                        </p>
                      </div>
                    )}
                    {transaction.type === 'remove' && (
                      <div className="flex items-start gap-2">
                        <div className="p-1 bg-red-100 dark:bg-red-900/20 rounded-full mt-0.5">
                          <ArrowUpDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </div>
                        <p>
                          <span className="font-medium">{transaction.amount} adet</span> ürün stoktan çıkarıldı. 
                          Stok miktarı <span className="font-medium">{transaction.previous_stock}</span> adetten 
                          <span className="font-medium"> {transaction.new_stock}</span> adete düşürüldü.
                        </p>
                      </div>
                    )}
                    {transaction.type === 'reset' && (
                      <div className="flex items-start gap-2">
                        <div className="p-1 bg-yellow-100 dark:bg-yellow-900/20 rounded-full mt-0.5">
                          <ArrowUpDown className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <p>
                          Ürün stok miktarı sıfırlandı. Önceki stok miktarı <span className="font-medium">{transaction.previous_stock}</span> adetti.
                        </p>
                      </div>
                    )}
                    {transaction.type === 'create' && (
                      <div className="flex items-start gap-2">
                        <div className="p-1 bg-blue-100 dark:bg-blue-900/20 rounded-full mt-0.5">
                          <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p>
                            Yeni ürün sisteme eklendi. Başlangıç stok miktarı <span className="font-medium">{transaction.new_stock}</span> adet olarak ayarlandı.
                          </p>
                          <div className="mt-2 pl-2 border-l-2 border-blue-200 dark:border-blue-800">
                            <p><span className="font-medium">Ürün Adı:</span> {transaction.product_name}</p>
                            <p><span className="font-medium">Konum:</span> {transaction.product_location}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {transaction.type === 'delete' && (
                      <div className="flex items-start gap-2">
                        <div className="p-1 bg-red-100 dark:bg-red-900/20 rounded-full mt-0.5">
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                          <p>
                            Ürün sistemden tamamen silindi. Silinen ürünün stok miktarı <span className="font-medium">{transaction.previous_stock}</span> adetti.
                          </p>
                          <div className="mt-2 pl-2 border-l-2 border-red-200 dark:border-red-800">
                            <p><span className="font-medium">Ürün Adı:</span> {transaction.product_name}</p>
                            <p><span className="font-medium">Konum:</span> {transaction.product_location}</p>
                            {transaction.is_deleted_record && transaction.original_created_at && (
                              <>
                                <p><span className="font-medium">Oluşturulma:</span> {formatDate(transaction.original_created_at)}</p>
                                {transaction.original_created_by && (
                                  <p><span className="font-medium">Oluşturan:</span> {transaction.original_created_by}</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {transaction.type === 'edit' && transaction.changes && (
                      <div className="flex items-start gap-2">
                        <div className="p-1 bg-purple-100 dark:bg-purple-900/20 rounded-full mt-0.5">
                          <Edit2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <p>Ürün bilgilerinde değişiklikler yapıldı:</p>
                          <ul className="list-disc list-inside space-y-1 mt-2 pl-2 border-l-2 border-purple-200 dark:border-purple-800">
                            {transaction.changes.name && (
                              <li>
                                <span className="font-medium">Ürün Adı:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.name.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.name.new}</span>
                              </li>
                            )}
                            {transaction.changes.location && (
                              <li>
                                <span className="font-medium">Konum:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.location.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.location.new}</span>
                              </li>
                            )}
                            {transaction.changes.description && (
                              <li>
                                <span className="font-medium">Açıklama:</span> Değiştirildi
                              </li>
                            )}
                            {transaction.changes.image_url && (
                              <li>
                                <span className="font-medium">Ürün Görseli:</span> Değiştirildi
                              </li>
                            )}
                            {transaction.changes.min_stock && (
                              <li>
                                <span className="font-medium">Minimum Stok:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.min_stock.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.min_stock.new}</span>
                              </li>
                            )}
                            {transaction.changes.max_stock && (
                              <li>
                                <span className="font-medium">Maksimum Stok:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.max_stock.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.max_stock.new}</span>
                              </li>
                            )}
                            {transaction.changes.category && (
                              <li>
                                <span className="font-medium">Kategori:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.category.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.category.new}</span>
                              </li>
                            )}
                            {transaction.changes.supplier && (
                              <li>
                                <span className="font-medium">Tedarikçi:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.supplier.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.supplier.new}</span>
                              </li>
                            )}
                            {transaction.changes.price && (
                              <li>
                                <span className="font-medium">Fiyat:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.price.old} TL</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.price.new} TL</span>
                              </li>
                            )}
                            {transaction.changes.unit && (
                              <li>
                                <span className="font-medium">Birim:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.unit.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.unit.new}</span>
                              </li>
                            )}
                            {transaction.changes.barcode && (
                              <li>
                                <span className="font-medium">Barkod:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.barcode.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.barcode.new}</span>
                              </li>
                            )}
                            {transaction.changes.sku && (
                              <li>
                                <span className="font-medium">SKU:</span> 
                                <span className="line-through text-gray-500 mx-1">{transaction.changes.sku.old}</span> 
                                <span className="text-purple-600 dark:text-purple-400">{transaction.changes.sku.new}</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* User and Date Info */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">İşlem Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">İşlemi Yapan:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{transaction.updated_by}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">İşlem Zamanı:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{formatDate(transaction.updated_at)}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {transaction.type === 'delete' && transaction.deleted_at && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Silinme:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">{formatDate(transaction.deleted_at)}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <Info className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">İşlem ID:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {transaction.id || `${transaction.product_id}-${new Date(transaction.updated_at).getTime()}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  const filteredTransactions = getFilteredTransactions();
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <History className="w-8 h-8 text-gray-800 dark:text-white mr-3" />
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">İşlem Geçmişi</h1>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        {/* Search and Filter Toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Ürün adı, konum veya kullanıcı ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isFilterActive()
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span>Filtreler</span>
              {getActiveFilterCount() > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-green-600 rounded-full ml-1">
                  {getActiveFilterCount()}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        <FilterPanel />

        {/* Active Filters Display */}
        {isFilterActive() && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Aktif Filtreler:</span>
            
            {filterConfig.operationType && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full flex items-center">
                {filterConfig.operationType === 'add' ? 'Stok Girişi' : 
                 filterConfig.operationType === 'remove' ? 'Stok Çıkışı' : 'Stok Sıfırlama'}
                <button 
                  onClick={() => setFilterConfig(prev => ({ ...prev, operationType: '' }))} 
                  className="ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {(filterConfig.dateRange.from || filterConfig.dateRange.to) && (
              <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full flex items-center">
                Tarih: {filterConfig.dateRange.from ? new Date(filterConfig.dateRange.from).toLocaleDateString() : '...'} 
                - 
                {filterConfig.dateRange.to ? new Date(filterConfig.dateRange.to).toLocaleDateString() : '...'}
                <button 
                  onClick={() => setFilterConfig(prev => ({ ...prev, dateRange: { from: '', to: '' } }))} 
                  className="ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.updatedBy && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full flex items-center">
                Kullanıcı: {filterConfig.updatedBy}
                <button 
                  onClick={() => setFilterConfig(prev => ({ ...prev, updatedBy: '' }))} 
                  className="ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.productName && (
              <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full flex items-center">
                Ürün: {filterConfig.productName}
                <button 
                  onClick={() => setFilterConfig(prev => ({ ...prev, productName: '' }))} 
                  className="ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.sortBy !== 'newest' && (
              <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded-full flex items-center">
                Sıralama: {filterConfig.sortBy === 'oldest' ? 'En Eski' : 'En Yeni'}
                <button 
                  onClick={() => setFilterConfig(prev => ({ ...prev, sortBy: 'newest' }))} 
                  className="ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            <button
              onClick={resetFilters}
              className="ml-auto text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Tümünü Temizle
            </button>
          </div>
        )}

        {/* Deleted Products Toggle */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <Trash2 className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Silinen Ürünleri Göster</span>
            {loadingDeleted && (
              <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
            )}
          </div>
          <button
            onClick={() => setShowDeletedProducts(!showDeletedProducts)}
            className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
              showDeletedProducts ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
            }`}
            role="switch"
            aria-checked={showDeletedProducts}
          >
            <span className="sr-only">Silinen ürünleri göster</span>
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${
                showDeletedProducts ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Transactions Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50">
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('product_name')}
                      className="group inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <span>Ürün</span>
                      <span className="ml-2 flex-none rounded">
                        {sortConfig.key === 'product_name' ? (
                          sortConfig.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-green-500" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 opacity-0 group-hover:opacity-50" />
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('type')}
                      className="group inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <span>İşlem</span>
                      <span className="ml-2 flex-none rounded">
                        {sortConfig.key === 'type' ? (
                          sortConfig.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-green-500" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 opacity-0 group-hover:opacity-50" />
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('stock_change')}
                      className="group inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <span>Stok Değişimi</span>
                      <span className="ml-2 flex-none rounded">
                        {sortConfig.key === 'stock_change' ? (
                          sortConfig.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-green-500" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 opacity-0 group-hover:opacity-50" />
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('updated_by')}
                      className="group inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <span>Kullanıcı</span>
                      <span className="ml-2 flex-none rounded">
                        {sortConfig.key === 'updated_by' ? (
                          sortConfig.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-green-500" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 opacity-0 group-hover:opacity-50" />
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort('updated_at')}
                      className="group inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <span>Tarih</span>
                      <span className="ml-2 flex-none rounded">
                        {sortConfig.key === 'updated_at' ? (
                          sortConfig.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-green-500" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 opacity-0 group-hover:opacity-50" />
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && loadingDeleted ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <History className="w-8 h-8 text-gray-400" />
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <p className="font-medium">İşlem bulunamadı</p>
                          <p className="mt-1">Filtreleri değiştirerek tekrar deneyin</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentTransactions.map((transaction, index) => (
                    <tr 
                      key={`${transaction.product_id}-${index}`}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        transaction.is_deleted_record ? 'bg-red-50 dark:bg-red-900/10' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                            {transaction.product_image ? (
                              <img 
                                src={transaction.product_image} 
                                alt={transaction.product_name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect width='18' height='18' x='3' y='3' rx='2' ry='2'/%3E%3Ccircle cx='9' cy='9' r='2'/%3E%3Cpath d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'/%3E%3C/svg%3E";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package2 className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {transaction.product_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {transaction.product_location}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          transaction.type === 'add'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : transaction.type === 'remove'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : transaction.type === 'create'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : transaction.type === 'edit'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : transaction.type === 'delete'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {transaction.type === 'add'
                            ? 'Stok Girişi'
                            : transaction.type === 'remove'
                            ? 'Stok Çıkışı'
                            : transaction.type === 'create'
                            ? 'Ürün Ekleme'
                            : transaction.type === 'edit'
                            ? 'Ürün Düzenleme'
                            : transaction.type === 'delete'
                            ? 'Ürün Silme'
                            : 'Stok Sıfırlama'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {transaction.previous_stock}
                          </span>
                          <ArrowUpDown className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {transaction.new_stock}
                          </span>
                          {transaction.type !== 'reset' && transaction.type !== 'delete' && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              ({transaction.type === 'add' || transaction.type === 'create' ? '+' : '-'}{transaction.amount})
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {transaction.updated_by}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatDate(transaction.updated_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => setSelectedTransaction(transaction)}
                          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Detayları Görüntüle"
                        >
                          <Info className="w-5 h-5 text-gray-400 hover:text-blue-500" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    Önceki
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    Sonraki
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Toplam <span className="font-medium">{filteredTransactions.length}</span> işlemden{' '}
                      <span className="font-medium">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{' '}
                      -{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, filteredTransactions.length)}
                      </span>{' '}
                      arası gösteriliyor
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                          currentPage === 1
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="sr-only">İlk Sayfa</span>
                        <ChevronDown className="h-5 w-5 transform rotate-90" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                          currentPage === 1
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="sr-only">Önceki</span>
                        <ChevronDown className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (totalPages <= 7) return true;
                          if (page === 1 || page === totalPages) return true;
                          if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                          return false;
                        })
                        .map((page, index, array) => {
                          if (index > 0 && array[index - 1] !== page - 1) {
                            return (
                              <React.Fragment key={`ellipsis-${page}`}>
                                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                                  ...
                                </span>
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                                    currentPage === page
                                      ? 'z-10 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-600 dark:text-green-400'
                                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          }
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-600 dark:text-green-400'
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                          currentPage === totalPages
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="sr-only">Sonraki</span>
                        <ChevronDown className="h-5 w-5 transform rotate-180" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                          currentPage === totalPages
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="sr-only">Son Sayfa</span>
                        <ChevronDown className="h-5 w-5 transform -rotate-90" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Transaction Details Modal */}
        {selectedTransaction && (
          <TransactionDetailsModal
            transaction={selectedTransaction}
            onClose={() => setSelectedTransaction(null)}
          />
        )}
      </div>
    </motion.div>
  );
};

export default Transactions; 